import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { vendorApi, api } from "../api/api";

/* ── Responsive hook ── */
const useBreakpoint = () => {
  const [bp, setBp] = useState({ xs: false, sm: false, md: false });
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      setBp({ xs: w < 480, sm: w < 640, md: w < 900 });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);
  return bp;
};

/* ── Design tokens ── */
const C = {
  primary:      "#1976d3",
  primaryDark:  "#1256a0",
  primaryLight: "#42a5f5",
  primaryBg:    "#e3f0fc",
  primaryBg2:   "#f0f7ff",
  surface:      "#ffffff",
  surfaceAlt:   "#f5f8fd",
  border:       "#dce8f5",
  text:         "#0d1b2a",
  textMid:      "#4a6080",
  textLight:    "#8facc4",
  error:        "#d32f2f",
  shadow:       "rgba(25,118,211,.13)",
};

const TYPE_META = {
  property:           { icon: "🏕️", desc: "Open land or residential plot" },
  project:            { icon: "🏗️", desc: "Gated plotted development" },
  "individual villa": { icon: "🏡", desc: "Independent house or bungalow" },
  apartment:          { icon: "🏢", desc: "Multi-storey flat complex" },
};

const toWizardId = (dbType = "") => {
  const t = dbType.toLowerCase();
  if (t === "property" || t === "land / plot" || t === "land/plot") return "property";
  if (t === "project" || t === "project plots") return "project";
  if (t === "individual villa") return "Individual Villa";
  if (t === "apartment" || t === "apartment flats") return "Apartment";
  return dbType;
};

const STEPS = ["Property Type", "Basic Info", "Details", "Media & Pricing", "Review"];

/* ── Input base ── */
const inputBase = {
  width: "100%", padding: "11px 14px",
  border: `1.5px solid ${C.border}`,
  borderRadius: 10, fontSize: 14, color: C.text,
  background: C.surfaceAlt, outline: "none",
  boxSizing: "border-box", transition: "all .2s",
  fontFamily: "'DM Sans', sans-serif",
};

/* ── Primitives ── */
const Field = ({ label, children, required, hint }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
    <label style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", color: C.textMid }}>
      {label}{required && <span style={{ color: C.error, marginLeft: 3 }}>*</span>}
    </label>
    {children}
    {hint && <span style={{ fontSize: 11, color: C.textLight }}>{hint}</span>}
  </div>
);

const Inp = ({ style, ...p }) => {
  const [f, setF] = useState(false);
  return (
    <input {...p}
      style={{ ...inputBase, ...(f ? { borderColor: C.primary, background: "#fff", boxShadow: `0 0 0 3px ${C.primaryBg}` } : {}), ...style }}
      onFocus={() => setF(true)} onBlur={() => setF(false)} />
  );
};

const Sel = ({ children, style, ...p }) => {
  const [f, setF] = useState(false);
  return (
    <select {...p}
      style={{
        ...inputBase, appearance: "none", cursor: "pointer", paddingRight: 36,
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%231976d3' stroke-width='1.8' fill='none'/%3E%3C/svg%3E")`,
        backgroundRepeat: "no-repeat", backgroundPosition: "right 14px center",
        ...(f ? { borderColor: C.primary, background: "#fff", boxShadow: `0 0 0 3px ${C.primaryBg}` } : {}), ...style
      }}
      onFocus={() => setF(true)} onBlur={() => setF(false)}>
      {children}
    </select>
  );
};

const Textarea = ({ style, ...p }) => {
  const [f, setF] = useState(false);
  return (
    <textarea {...p}
      style={{ ...inputBase, resize: "vertical", ...(f ? { borderColor: C.primary, background: "#fff", boxShadow: `0 0 0 3px ${C.primaryBg}` } : {}), ...style }}
      onFocus={() => setF(true)} onBlur={() => setF(false)} />
  );
};

const Btn = ({ children, variant = "primary", style, ...p }) => {
  const [hov, setHov] = useState(false);
  const base = { padding: "11px 22px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", transition: "all .2s", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.02em", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 };
  const v = {
    primary: { background: hov ? C.primaryDark : `linear-gradient(135deg,${C.primary},${C.primaryLight})`, color: "#fff", border: "none", boxShadow: hov ? `0 6px 24px ${C.shadow}` : `0 3px 12px ${C.shadow}`, transform: hov ? "translateY(-1px)" : "none" },
    outline: { background: "transparent", border: `1.5px solid ${C.border}`, color: C.textMid, boxShadow: hov ? "0 3px 10px rgba(0,0,0,.06)" : "none", transform: hov ? "translateY(-1px)" : "none" },
    danger:  { background: "transparent", border: "1.5px solid #ffcdd2", color: C.error },
  };
  return (
    <button {...p} onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ ...base, ...v[variant], ...style }}>
      {children}
    </button>
  );
};

/* ── Grid helper — collapses to 1-col on mobile ── */
const Grid = ({ cols = 2, children, bp }) => (
  <div style={{
    display: "grid",
    gridTemplateColumns: bp?.sm ? "1fr" : `repeat(${cols}, 1fr)`,
    gap: bp?.sm ? 14 : 16,
  }}>
    {children}
  </div>
);

/* ── Step bar ── */
const StepBar = ({ current, bp }) => {
  if (bp?.xs) {
    /* mobile: compact pill */
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28 }}>
        <div style={{ display: "flex", gap: 6 }}>
          {STEPS.map((_, i) => (
            <div key={i} style={{
              width: i === current ? 22 : 8, height: 8, borderRadius: 99,
              background: i <= current ? C.primary : "#e0eaf5",
              transition: "all .3s",
            }} />
          ))}
        </div>
        <span style={{ fontSize: 12, fontWeight: 700, color: C.textMid, letterSpacing: "0.05em" }}>
          {current + 1}/{STEPS.length} · {STEPS[current]}
        </span>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 36, overflowX: "auto", paddingBottom: 4 }}>
      {STEPS.map((s, i) => {
        const done = i < current, active = i === current;
        return (
          <React.Fragment key={s}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6, minWidth: bp?.md ? 48 : 58 }}>
              <div style={{
                width: 33, height: 33, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 700, transition: "all .3s",
                background: done ? `linear-gradient(135deg,${C.primary},${C.primaryLight})` : active ? C.primary : "#e0eaf5",
                color: done || active ? "#fff" : C.textLight,
                boxShadow: active ? `0 0 0 5px ${C.primaryBg}` : "none",
              }}>
                {done ? "✓" : i + 1}
              </div>
              {!bp?.md && (
                <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: active ? C.primary : done ? C.primaryDark : C.textLight, whiteSpace: "nowrap" }}>
                  {s}
                </span>
              )}
            </div>
            {i < STEPS.length - 1 && (
              <div style={{ flex: 1, height: 2, margin: bp?.md ? "0 2px" : "0 4px", marginBottom: bp?.md ? 0 : 22, minWidth: 12,
                background: i < current ? `linear-gradient(90deg,${C.primary},${C.primaryLight})` : "#e0eaf5", transition: "background .4s" }} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ── Dropzone ── */
const Dropzone = ({ label, multiple, onFiles, files }) => {
  const ref = useRef();
  const [drag, setDrag] = useState(false);
  const previews = files ? (Array.isArray(files) ? files : [files]) : [];
  return (
    <div>
      <div
        onClick={() => ref.current.click()}
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={e => { e.preventDefault(); setDrag(false); const f = Array.from(e.dataTransfer.files); onFiles(multiple ? f : f[0]); }}
        style={{
          border: `2px dashed ${drag ? C.primary : C.border}`, borderRadius: 12,
          padding: "22px 16px", textAlign: "center", cursor: "pointer",
          background: drag ? C.primaryBg2 : C.surfaceAlt, transition: "all .2s",
        }}>
        <div style={{ fontSize: 26, marginBottom: 6 }}>📸</div>
        <div style={{ fontSize: 13, color: C.textMid, fontWeight: 600 }}>{label}</div>
        <div style={{ fontSize: 11, color: C.textLight, marginTop: 3 }}>Tap or drag & drop{multiple ? " (multiple)" : ""}</div>
      </div>
      <input ref={ref} type="file" hidden multiple={multiple} accept="image/*"
        onChange={e => onFiles(multiple ? Array.from(e.target.files) : e.target.files[0])} />
      {previews.length > 0 && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
          {previews.map((f, i) => (
            <div key={i} style={{ position: "relative" }}>
              <img src={URL.createObjectURL(f)} alt=""
                style={{ width: 72, height: 72, objectFit: "cover", borderRadius: 8, border: `2px solid ${C.border}` }} />
              <div style={{ position: "absolute", top: -5, right: -5, width: 18, height: 18, borderRadius: "50%",
                background: C.primary, color: "#fff", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700 }}>✓</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Toast ── */
const Toast = ({ msg, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 4200); return () => clearTimeout(t); }, []);
  return (
    <div style={{
      position: "fixed", bottom: 16, right: 16, left: 16, zIndex: 9999,
      background: type === "success" ? "#0d2418" : "#2a0d0d",
      color: "#fff", padding: "14px 18px", borderRadius: 12, fontSize: 14, fontWeight: 600,
      boxShadow: "0 8px 32px rgba(0,0,0,.22)", display: "flex", alignItems: "center", gap: 10,
      animation: "toastIn .3s cubic-bezier(.34,1.56,.64,1)",
      maxWidth: 480, margin: "0 auto",
    }}>
      <span>{type === "success" ? "✅" : "❌"}</span>
      <span style={{ flex: 1 }}>{msg}</span>
      <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,.5)", cursor: "pointer", fontSize: 20, padding: 0, lineHeight: 1 }}>×</button>
    </div>
  );
};

/* ── Amenity builder ── */
const AmenityBuilder = ({ amenities, setAmenities }) => (
  <Field label="Amenities" hint="Add facilities available in the complex">
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {amenities.map((a, i) => (
        <div key={i} style={{ display: "flex", gap: 8 }}>
          <Inp placeholder="e.g. Swimming Pool, Gym, Club House…" value={a.name}
            onChange={e => { const u = [...amenities]; u[i].name = e.target.value; setAmenities(u); }} />
          {amenities.length > 1 && (
            <Btn variant="danger" style={{ padding: "11px 14px", fontSize: 16, flexShrink: 0 }}
              onClick={() => setAmenities(amenities.filter((_, j) => j !== i))}>×</Btn>
          )}
        </div>
      ))}
      <Btn variant="outline" style={{ width: "fit-content", fontSize: 13, padding: "8px 16px" }}
        onClick={() => setAmenities([...amenities, { name: "" }])}>
        + Add Amenity
      </Btn>
    </div>
  </Field>
);

/* ── Section header ── */
const SH = ({ title, sub, bp }) => (
  <div style={{ marginBottom: 8 }}>
    <h2 style={{ fontFamily: "'Playfair Display', serif", fontSize: bp?.xs ? 18 : 22, fontWeight: 700, color: C.text, margin: "0 0 4px" }}>{title}</h2>
    <p style={{ fontSize: 13, color: C.textMid, margin: 0 }}>{sub}</p>
  </div>
);

/* ══════════════════════════════ MAIN ══════════════════════════════ */
export default function AddProperty() {
  const isVerified = useSelector((state) => state.user.isVerified);
  const navigate   = useNavigate();
  const bp         = useBreakpoint();

  const [step, setStep]             = useState(0);
  const [toast, setToast]           = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [propertyType, setPropertyType] = useState("");
  const [form, setForm] = useState({ propertyName: "", city: "", address: "", facing: "", NoOfPlots: "", sizeInSqft: "", sqftPrice: "" });
  const [totalArea,     setTotalArea]     = useState("");
  const [totalPrice,    setTotalPrice]    = useState("");
  const [landarea,      setLandArea]      = useState("");
  const [noofFloors,    setNoOfFloors]    = useState("");
  const [bedroomcount,  setBedRoomCount]  = useState("");
  const [bathroomcount, setBathRoomCount] = useState("");
  const [parking,       setParking]       = useState("yes");
  const [parkingType,   setParkingType]   = useState("car");
  const [ageOfBuilding, setAgeOfBuilding] = useState("");
  const [waterSupply,   setWaterSupply]   = useState("");
  const [balcony,       setBalcony]       = useState("");
  const [powerBackup,   setPowerBackup]   = useState("");
  const [constructionStatus, setConstructionStatus] = useState("");
  const [amenities,     setAmenities]     = useState([{ name: "" }]);
  const [description,   setDescription]  = useState("");
  const [priceMode,     setPriceMode]     = useState("");
  const [extraValue,    setExtraValue]    = useState("");
  const [displayPicture,  setDisplayPicture]  = useState(null);
  const [galleryImages,   setGalleryImages]   = useState([]);
  const [layoutPicture,   setLayoutPicture]   = useState(null);
  const [galleryError,    setGalleryError]    = useState("");
  const [propertyTypes,   setPropertyTypes]   = useState([]);

  useEffect(() => {
    api.getPropertyTypes()
      .then(res => {
        const types = (res.data || []).map(t => ({
          id:    toWizardId(t.PropertyType),
          label: t.PropertyType,
          icon:  TYPE_META[toWizardId(t.PropertyType).toLowerCase()]?.icon ?? "🏠",
          desc:  TYPE_META[toWizardId(t.PropertyType).toLowerCase()]?.desc ?? "",
        }));
        setPropertyTypes(types);
      })
      .catch(err => console.error("Failed to load property types", err));
  }, []);

  const handleChange = e => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const basePrice = (Number(form.sizeInSqft) || 0) * (Number(form.sqftPrice) || 0);
  const effectivePrice = propertyType === "property" ? basePrice : (Number(totalPrice) || 0);
  const commission =
    priceMode === "percentage" && extraValue && effectivePrice ? (effectivePrice * Number(extraValue)) / 100 :
    priceMode === "sqft" && extraValue && form.sizeInSqft ? Number(extraValue) * Number(form.sizeInSqft) : 0;

  const err = msg => { setToast({ msg, type: "error" }); return false; };
  const validateStep = () => {
    if (step === 0 && !propertyType) return err("Please select a property type.");
    if (step === 1) {
      if (!form.propertyName || !form.city || !form.address) return err("Please fill in all required fields.");
      if (!["project", "Apartment"].includes(propertyType) && !form.facing) return err("Facing direction is required.");
    }
    if (step === 2) {
      if (propertyType === "project" && (!form.NoOfPlots || !totalArea)) return err("Number of plots and total area are required.");
      if (propertyType === "property" && (!form.sizeInSqft || !form.sqftPrice)) return err("Size and price per sq.ft are required.");
      if (propertyType === "Individual Villa" && (!totalPrice || !form.sizeInSqft || !bedroomcount || !bathroomcount || !landarea || !noofFloors || !waterSupply || !powerBackup)) return err("Please fill in all villa details.");
      if (propertyType === "Apartment" && (!landarea || !noofFloors || !form.NoOfPlots)) return err("Land area, floors and total flats are required.");
    }
    if (step === 3) {
      if (!displayPicture) return err("Display picture is required.");
      if (galleryImages.length < 3) return err("Please upload at least 3 gallery images.");
      if (propertyType === "project" && !layoutPicture) return err("Layout picture is required for projects.");
    }
    return true;
  };

  const goNext = () => { if (validateStep()) { setStep(s => Math.min(s + 1, STEPS.length - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); } };
  const goBack = () => { setStep(s => Math.max(s - 1, 0)); window.scrollTo({ top: 0, behavior: "smooth" }); };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("PropertyType", propertyType);
      fd.append("PropertyName", form.propertyName);
      fd.append("City", form.city);
      fd.append("Address", form.address);
      fd.append("Facing", form.facing);
      fd.append("Description", description);
      fd.append("PriceMode", priceMode);
      fd.append("ExtraValue", extraValue);
      if (propertyType === "project") { fd.append("LayoutURL", layoutPicture); fd.append("NoOfPlots", form.NoOfPlots); fd.append("TotalArea", totalArea); }
      else if (propertyType === "property") { fd.append("SizeInSqFt", form.sizeInSqft); fd.append("SqFtPrice", form.sqftPrice); }
      else if (propertyType === "Individual Villa") { fd.append("TotalPrice", totalPrice); fd.append("SizeInSqFt", form.sizeInSqft); fd.append("ExtraData", JSON.stringify({ Bedrooms: bedroomcount, Bathrooms: bathroomcount, ParkingAvailable: parking, ParkingType: parkingType, LandAreaInSqFt: landarea, NoOfFloors: noofFloors, WaterSupply: waterSupply, PowerBackUp: powerBackup, AgeOfBuilding: ageOfBuilding, Balcony: balcony })); }
      else if (propertyType === "Apartment") { fd.append("ApartmentName", form.propertyName); fd.append("TotalUnits", form.NoOfPlots); fd.append("LandArea", landarea); fd.append("TotalFloors", noofFloors); fd.append("ConstructionStatus", constructionStatus); fd.append("Amenities", JSON.stringify(amenities.filter(a => a.name.trim()).map(a => ({ name: a.name.trim() })))); }
      fd.append("iconUrl", displayPicture);
      galleryImages.forEach(img => fd.append("images[]", img));
      const response = await vendorApi.addPropertyByVendorId(fd);
      if (response.status === 200) {
        setToast({ msg: "Property published successfully! 🎉", type: "success" });
        setTimeout(() => navigate("/profile/properties"), 2200);
      } else {
        setToast({ msg: "Something went wrong. Please try again.", type: "error" });
      }
    } catch (e) {
      setToast({ msg: e?.response?.data?.message || "Failed to submit. Please try again.", type: "error" });
    } finally { setSubmitting(false); }
  };

  /* ── Step renderers ── */
  const renderStep = () => {
    switch (step) {

      case 0: return (
        <div>
          <SH bp={bp} title="What are you listing?" sub="Select the category that best matches your property." />
          <div style={{
            display: "grid",
            gridTemplateColumns: bp?.xs ? "1fr 1fr" : bp?.sm ? "repeat(2,1fr)" : "repeat(auto-fill,minmax(180px,1fr))",
            gap: bp?.xs ? 10 : 14,
            marginTop: 20,
          }}>
            {propertyTypes.map(t => {
              const sel = propertyType === t.id;
              return (
                <div key={t.id} onClick={() => setPropertyType(t.id)} style={{
                  border: `2px solid ${sel ? C.primary : C.border}`, borderRadius: 14,
                  padding: bp?.xs ? "16px 10px" : "22px 16px", cursor: "pointer", textAlign: "center",
                  background: sel ? C.primaryBg2 : C.surfaceAlt,
                  boxShadow: sel ? `0 6px 28px ${C.shadow}` : "none",
                  transform: sel ? "translateY(-3px)" : "none", transition: "all .22s",
                }}>
                  <div style={{ fontSize: bp?.xs ? 26 : 32, marginBottom: 8 }}>{t.icon}</div>
                  <div style={{ fontWeight: 700, fontSize: bp?.xs ? 12 : 14, color: C.text, marginBottom: 4 }}>{t.label}</div>
                  {!bp?.xs && <div style={{ fontSize: 11, color: C.textLight }}>{t.desc}</div>}
                  {sel && (
                    <div style={{ marginTop: 10, width: 20, height: 20, borderRadius: "50%", background: `linear-gradient(135deg,${C.primary},${C.primaryLight})`, display: "flex", alignItems: "center", justifyContent: "center", margin: "10px auto 0", color: "#fff", fontSize: 11, fontWeight: 700 }}>✓</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );

      case 1: return (
        <div style={{ display: "flex", flexDirection: "column", gap: bp?.xs ? 16 : 20 }}>
          <SH bp={bp} title="Basic Information" sub="Tell us the essentials about your property." />
          <Field label={propertyType === "project" ? "Project Name" : propertyType === "Apartment" ? "Apartment Name" : "Property Name"} required>
            <Inp name="propertyName" value={form.propertyName} onChange={handleChange} placeholder="e.g. Green Valley Residences" />
          </Field>
          <Grid cols={2} bp={bp}>
            <Field label="City" required>
              <Inp name="city" value={form.city} onChange={handleChange} placeholder="e.g. Hyderabad" />
            </Field>
            {!["project", "Apartment"].includes(propertyType) && (
              <Field label="Facing Direction" required>
                <Sel name="facing" value={form.facing} onChange={handleChange}>
                  <option value="">Select direction</option>
                  {["East", "West", "North", "South", "North-East", "North-West", "South-East", "South-West"].map(f => <option key={f} value={f}>{f}</option>)}
                </Sel>
              </Field>
            )}
          </Grid>
          <Field label="Full Address" required>
            <Textarea name="address" value={form.address} onChange={handleChange} placeholder="Street, landmark, locality…" rows={3} />
          </Field>
          <Field label="Description" hint="Optional — helps buyers understand your property better">
            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Highlight key features, nearby facilities…" rows={4} />
          </Field>
        </div>
      );

      case 2: {
        const typeLabel = propertyTypes.find(t => t.id === propertyType)?.label;
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: bp?.xs ? 16 : 20 }}>
            <SH bp={bp} title="Property Details" sub={`Specific details for your ${typeLabel}.`} />

            {propertyType === "property" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <Grid cols={2} bp={bp}>
                  <Field label="Size in Sq.Ft" required><Inp name="sizeInSqft" type="number" value={form.sizeInSqft} onChange={handleChange} placeholder="1200" /></Field>
                  <Field label="Price per Sq.Ft (₹)" required><Inp name="sqftPrice" type="number" value={form.sqftPrice} onChange={handleChange} placeholder="850" /></Field>
                </Grid>
                {basePrice > 0 && (
                  <div style={{ background: C.primaryBg2, border: `1.5px solid ${C.border}`, borderRadius: 12, padding: "14px 18px" }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.textMid, marginBottom: 4 }}>Estimated Total Value</div>
                    <div style={{ fontSize: bp?.xs ? 20 : 26, fontWeight: 800, color: C.primary }}>₹ {basePrice.toLocaleString("en-IN")}</div>
                  </div>
                )}
              </div>
            )}

            {propertyType === "project" && (
              <Grid cols={2} bp={bp}>
                <Field label="Number of Plots" required><Inp name="NoOfPlots" type="number" value={form.NoOfPlots} onChange={handleChange} placeholder="50" /></Field>
                <Field label="Total Area (Acres / Sq.Yd)" required><Inp value={totalArea} onChange={e => setTotalArea(e.target.value)} placeholder="5 Acres" /></Field>
              </Grid>
            )}

            {propertyType === "Individual Villa" && (
              <Grid cols={2} bp={bp}>
                <Field label="Built-up Area (Sq.Ft)" required><Inp name="sizeInSqft" type="number" value={form.sizeInSqft} onChange={handleChange} placeholder="2400" /></Field>
                <Field label="Total Price (₹)" required><Inp type="number" value={totalPrice} onChange={e => setTotalPrice(e.target.value)} placeholder="8500000" /></Field>
                <Field label="Land Area" required><Inp value={landarea} onChange={e => setLandArea(e.target.value)} placeholder="200 Sq.Yd" /></Field>
                <Field label="No. of Floors" required><Inp type="number" value={noofFloors} onChange={e => setNoOfFloors(e.target.value)} placeholder="2" /></Field>
                <Field label="Bedrooms" required><Inp type="number" value={bedroomcount} onChange={e => setBedRoomCount(e.target.value)} placeholder="3" /></Field>
                <Field label="Bathrooms" required><Inp type="number" value={bathroomcount} onChange={e => setBathRoomCount(e.target.value)} placeholder="2" /></Field>
                <Field label="Age of Building (yrs)"><Inp type="number" value={ageOfBuilding} onChange={e => setAgeOfBuilding(e.target.value)} placeholder="0" /></Field>
                <Field label="Balcony Count"><Inp type="number" value={balcony} onChange={e => setBalcony(e.target.value)} placeholder="1" /></Field>
                <Field label="Water Supply" required><Inp value={waterSupply} onChange={e => setWaterSupply(e.target.value)} placeholder="Municipal / Borewell" /></Field>
                <Field label="Power Backup" required><Inp value={powerBackup} onChange={e => setPowerBackup(e.target.value)} placeholder="Full / Partial" /></Field>
                <Field label="Parking Available">
                  <Sel value={parking} onChange={e => setParking(e.target.value)}>
                    <option value="yes">Yes</option><option value="no">No</option>
                  </Sel>
                </Field>
                {parking === "yes" && (
                  <Field label="Parking Type">
                    <Sel value={parkingType} onChange={e => setParkingType(e.target.value)}>
                      <option value="car">Car</option><option value="bike">Bike</option><option value="both">Both</option>
                    </Sel>
                  </Field>
                )}
              </Grid>
            )}

            {propertyType === "Apartment" && (
              <>
                <Grid cols={2} bp={bp}>
                  <Field label="Total Land Area" required><Inp value={landarea} onChange={e => setLandArea(e.target.value)} placeholder="2 Acres" /></Field>
                  <Field label="Total Flats / Units" required><Inp name="NoOfPlots" type="number" value={form.NoOfPlots} onChange={handleChange} placeholder="120" /></Field>
                  <Field label="Total Floors" required><Inp type="number" value={noofFloors} onChange={e => setNoOfFloors(e.target.value)} placeholder="15" /></Field>
                  <Field label="Construction Status">
                    <Sel value={constructionStatus} onChange={e => setConstructionStatus(e.target.value)}>
                      <option value="">Select status</option>
                      <option value="Planning">Planning</option>
                      <option value="Under Construction">Under Construction</option>
                      <option value="Ready to Move">Ready to Move</option>
                    </Sel>
                  </Field>
                </Grid>
                <div style={{ borderTop: `1.5px solid ${C.border}`, paddingTop: 20 }}>
                  <AmenityBuilder amenities={amenities} setAmenities={setAmenities} />
                </div>
              </>
            )}
          </div>
        );
      }

      case 3: {
        const showCommission = ["property", "Individual Villa", "Apartment"].includes(propertyType);
        return (
          <div style={{ display: "flex", flexDirection: "column", gap: bp?.xs ? 18 : 24 }}>
            <SH bp={bp} title="Photos & Pricing" sub="Great photos attract more buyers — upload your best shots!" />
            {propertyType === "project" && (
              <Field label="Layout / Master Plan" required>
                <Dropzone label="Upload layout image" onFiles={setLayoutPicture} files={layoutPicture ? [layoutPicture] : []} />
              </Field>
            )}
            <Field label="Display / Cover Photo" required>
              <Dropzone label="Upload main display photo" onFiles={f => setDisplayPicture(Array.isArray(f) ? f[0] : f)} files={displayPicture ? [displayPicture] : []} />
            </Field>
            <Field label="Gallery Images" required hint="Minimum 3 images required">
              <Dropzone label="Upload gallery photos" multiple
                onFiles={f => { const u = [...galleryImages, ...(Array.isArray(f) ? f : [f])]; setGalleryImages(u); setGalleryError(u.length < 3 ? "Please upload at least 3 images." : ""); }}
                files={galleryImages} />
              {galleryError && <span style={{ color: C.error, fontSize: 12 }}>{galleryError}</span>}
            </Field>
            {showCommission && (
              <div style={{ background: C.primaryBg2, border: `1.5px solid ${C.border}`, borderRadius: 16, padding: bp?.xs ? 14 : 20 }}>
                <div style={{ fontWeight: 700, fontSize: 11, color: C.textMid, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 16 }}>💼 Commission Setup</div>
                <Grid cols={2} bp={bp}>
                  <Field label="Commission Type">
                    <Sel value={priceMode} onChange={e => { setPriceMode(e.target.value); setExtraValue(""); }}>
                      <option value="">None</option>
                      <option value="percentage">Percentage (%)</option>
                      <option value="sqft">Per Sq.Ft (₹)</option>
                    </Sel>
                  </Field>
                  {priceMode && (
                    <Field label={priceMode === "percentage" ? "Percentage Value (%)" : "Amount per Sq.Ft (₹)"}>
                      <Inp type="number" value={extraValue} onChange={e => setExtraValue(e.target.value)} placeholder={priceMode === "percentage" ? "e.g. 2" : "e.g. 50"} />
                    </Field>
                  )}
                </Grid>
                {commission > 0 && (
                  <div style={{ marginTop: 14, padding: "12px 16px", background: "#fff", border: `1.5px solid ${C.border}`, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                    <span style={{ fontSize: 13, color: C.textMid, fontWeight: 600 }}>Estimated Commission</span>
                    <span style={{ fontSize: bp?.xs ? 16 : 20, fontWeight: 800, color: C.primary }}>₹ {commission.toLocaleString("en-IN")}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      }

      case 4: {
        const typeLabel = propertyTypes.find(t => t.id === propertyType)?.label;
        const rows = [
          ["Property Type", typeLabel],
          ["Name", form.propertyName],
          ["City", form.city],
          ["Address", form.address],
          form.facing && ["Facing", form.facing],
          propertyType === "property" && form.sizeInSqft && ["Size", `${Number(form.sizeInSqft).toLocaleString("en-IN")} Sq.Ft`],
          propertyType === "property" && form.sqftPrice && ["Price / Sq.Ft", `₹ ${Number(form.sqftPrice).toLocaleString("en-IN")}`],
          propertyType === "property" && basePrice && ["Total Value", `₹ ${basePrice.toLocaleString("en-IN")}`],
          propertyType === "project" && form.NoOfPlots && ["No. of Plots", form.NoOfPlots],
          propertyType === "project" && totalArea && ["Total Area", totalArea],
          propertyType === "Individual Villa" && totalPrice && ["Total Price", `₹ ${Number(totalPrice).toLocaleString("en-IN")}`],
          propertyType === "Individual Villa" && bedroomcount && ["Bedrooms", bedroomcount],
          propertyType === "Individual Villa" && bathroomcount && ["Bathrooms", bathroomcount],
          propertyType === "Apartment" && form.NoOfPlots && ["Total Flats", form.NoOfPlots],
          propertyType === "Apartment" && noofFloors && ["Floors", noofFloors],
          propertyType === "Apartment" && constructionStatus && ["Construction", constructionStatus],
          priceMode && ["Commission", priceMode === "percentage" ? `${extraValue}%` : `₹${extraValue}/Sq.Ft`],
          commission > 0 && ["Commission Amt", `₹ ${commission.toLocaleString("en-IN")}`],
          displayPicture && ["Cover Photo", "✓ Uploaded"],
          galleryImages.length && [`Gallery (${galleryImages.length})`, "✓ Uploaded"],
        ].filter(Boolean);
        return (
          <div>
            <SH bp={bp} title="Review & Publish" sub="Everything look good? Hit publish to go live." />
            <div style={{ marginTop: 20, borderRadius: 14, overflow: "hidden", border: `1.5px solid ${C.border}` }}>
              {rows.map(([k, v], i) => (
                <div key={i} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                  padding: bp?.xs ? "10px 14px" : "12px 18px",
                  background: i % 2 === 0 ? C.surfaceAlt : "#fff", gap: 12, flexWrap: bp?.xs ? "wrap" : "nowrap",
                }}>
                  <span style={{ fontSize: 12, color: C.textMid, fontWeight: 600, minWidth: bp?.xs ? "auto" : 140 }}>{k}</span>
                  <span style={{ fontSize: 12, color: C.text, fontWeight: 500, textAlign: bp?.xs ? "left" : "right" }}>{v}</span>
                </div>
              ))}
            </div>
            {description && (
              <div style={{ marginTop: 16, background: C.surfaceAlt, border: `1.5px solid ${C.border}`, borderRadius: 12, padding: bp?.xs ? "12px 14px" : "14px 18px" }}>
                <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.07em", color: C.textMid, marginBottom: 8 }}>Description</div>
                <p style={{ fontSize: 13, color: C.text, lineHeight: 1.75, margin: 0 }}>{description}</p>
              </div>
            )}
            <div style={{ marginTop: 20, background: C.primaryBg2, border: `1.5px solid ${C.border}`, borderRadius: 14, padding: bp?.xs ? "14px 16px" : "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ fontSize: 26 }}>🚀</div>
              <div>
                <div style={{ fontWeight: 700, color: C.text, fontSize: 14 }}>Ready to publish?</div>
                <div style={{ fontSize: 12, color: C.textMid, marginTop: 2 }}>Your listing will be reviewed and go live within minutes.</div>
              </div>
            </div>
          </div>
        );
      }

      default: return null;
    }
  };

  if (!isVerified) return (
    <div style={{ maxWidth: 440, margin: "60px auto", textAlign: "center", padding: bp?.xs ? 24 : 40, background: "#fff", borderRadius: 20, boxShadow: `0 8px 48px ${C.shadow}` }}>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
      <h2 style={{ color: C.text, marginBottom: 8, fontFamily: "'Playfair Display', serif" }}>Account Not Verified</h2>
      <p style={{ color: C.textMid }}>Please verify your email to start listing properties.</p>
      <Btn onClick={() => navigate("/myprofile")} style={{ marginTop: 20 }}>Go to Profile</Btn>
    </div>
  );

  const cardPadding = bp?.xs ? "22px 16px" : bp?.sm ? "28px 22px" : "36px 40px";

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        body { margin: 0; font-family: 'DM Sans', sans-serif; background: #eef4fc; }
        select option { background: #fff; }
        textarea { font-family: 'DM Sans', sans-serif; }
        @keyframes toastIn { from { transform: translateX(80px); opacity: 0; } to { transform: none; opacity: 1; } }
        @keyframes fadeUp  { from { transform: translateY(24px); opacity: 0; } to { transform: none; opacity: 1; } }
        @keyframes slideIn { from { transform: translateX(28px); opacity: 0; } to { transform: none; opacity: 1; } }
        @keyframes spin    { to { transform: rotate(360deg); } }
      `}</style>

      {toast && <Toast {...toast} onClose={() => setToast(null)} />}

      <div style={{ minHeight: "100vh", background: "#eef4fc", paddingBottom: 80 }}>

        {/* ── Header ── */}
        <div style={{
          background: `linear-gradient(135deg,#0d47a1 0%,${C.primary} 55%,${C.primaryLight} 100%)`,
          padding: bp?.xs ? "0 14px" : "0 28px",
          display: "flex", alignItems: "center", gap: 0,
          boxShadow: `0 4px 24px ${C.shadow}`,
          position: "sticky", top: 0, zIndex: 100,
          height: bp?.xs ? 54 : 62,
        }}>
          <div style={{ width: 4, height: "100%", background: "rgba(255,255,255,.25)", borderRadius: 2, marginRight: bp?.xs ? 12 : 20 }} />
          <div style={{ display: "flex", alignItems: "center", gap: bp?.xs ? 8 : 13, flex: 1, minWidth: 0 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "rgba(255,255,255,.18)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>🏠</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: "'Playfair Display', serif", color: "#fff", fontSize: bp?.xs ? 15 : 18, fontWeight: 700, lineHeight: 1.2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>Add New Property</div>
              {!bp?.xs && <div style={{ color: "rgba(255,255,255,.6)", fontSize: 11, marginTop: 1 }}>List your property and reach thousands of buyers</div>}
            </div>
          </div>
          <div style={{ background: "rgba(255,255,255,.15)", borderRadius: 20, padding: "5px 12px", color: "#fff", fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", flexShrink: 0 }}>
            {step + 1}/{STEPS.length}
          </div>
        </div>

        {/* ── Card ── */}
        <div style={{ maxWidth: 820, margin: bp?.xs ? "16px auto" : "28px auto", padding: bp?.xs ? "0 10px" : "0 16px" }}>
          <div style={{ background: "#fff", borderRadius: bp?.xs ? 16 : 20, boxShadow: `0 4px 40px ${C.shadow}`, padding: cardPadding, animation: "fadeUp .4s ease" }}>
            <StepBar current={step} bp={bp} />
            <div key={step} style={{ animation: "slideIn .28s ease" }}>
              {renderStep()}
            </div>

            {/* ── Nav buttons ── */}
            <div style={{
              display: "flex",
              flexDirection: bp?.xs ? "column-reverse" : "row",
              justifyContent: "space-between",
              alignItems: bp?.xs ? "stretch" : "center",
              gap: bp?.xs ? 10 : 12,
              marginTop: 36, paddingTop: 24,
              borderTop: `1.5px solid ${C.border}`,
            }}>
              <Btn
                variant="outline"
                onClick={goBack}
                style={{
                  visibility: step === 0 ? "hidden" : "visible",
                  width: bp?.xs ? "100%" : "auto",
                }}
              >
                ← Back
              </Btn>

              <div style={{ display: "flex", alignItems: "center", gap: 10, flexDirection: bp?.xs ? "column" : "row", width: bp?.xs ? "100%" : "auto" }}>
                {!bp?.xs && <span style={{ fontSize: 11, color: C.textLight }}>{STEPS[step]}</span>}
                {step < STEPS.length - 1 ? (
                  <Btn onClick={goNext} style={{ width: bp?.xs ? "100%" : "auto" }}>Continue →</Btn>
                ) : (
                  <Btn onClick={handleSubmit} disabled={submitting} style={{ opacity: submitting ? 0.72 : 1, minWidth: bp?.xs ? "auto" : 168, width: bp?.xs ? "100%" : "auto" }}>
                    {submitting
                      ? <><span style={{ width: 14, height: 14, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%", display: "inline-block", animation: "spin .7s linear infinite" }} /> Publishing…</>
                      : "🚀 Publish Property"}
                  </Btn>
                )}
              </div>
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: 14, color: C.textLight, fontSize: 11 }}>
            🔒 Your data is secure and will not be shared without your consent.
          </div>
        </div>
      </div>
    </>
  );
}
